import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Link,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import API, { storeUserData } from "../utils/api";

const Login = () => {
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const usernameRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
const registered = location.state?.registered;
  const { login } = useAuth();

  // Autofocus username on page load
  useEffect(() => {
    usernameRef.current?.focus();
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.username.trim() || !formData.password.trim()) {
      setError("Please enter your username and password.");
      return;
    }

    try {
      const res = await API.post("/token/", formData, {
        headers: { "Content-Type": "application/json" },
      });

      const data = res.data;

      if (!data?.access) {
        setError("Unexpected login response. Please try again.");
        return;
      }

      login({ access: data.access, refresh: data.refresh });

      // Fetch user profile after login
      try {
        const profileRes = await API.get("/users/me/", {
          headers: { Authorization: `Bearer ${data.access}` },
        });
        if (profileRes.status === 200) {
          // ✅ Store user data with is_read_only ensured
          storeUserData(profileRes.data);
        }
      } catch (err) {
        console.warn("Could not fetch user profile:", err);
      }

      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError("Login failed. Please check your username and password.");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f1f5f9",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: 420,
          borderRadius: 4,
          textAlign: "center",
          background:
  "linear-gradient(180deg, rgba(255,255,255,0.95), rgba(248,250,252,0.95))",
backdropFilter: "blur(10px)",
border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {/*----------------------------------------------------- Logo + Branding */}
<Box sx={{ mb: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
  <img
    src="/territory-logo.png"
    alt="Territory CRM"
    style={{
      width: 280,
      maxWidth: "100%",
      marginBottom: 14,
    }}
  />

  <Typography
    
    sx={{
      color: "text.secondary",
      fontSize: 13,
    }}
  >
    Sign in to continue
  </Typography>
</Box>

   {registered && (
  <Typography
    sx={{
      mb: 2,
      fontSize: 14,
      color: "#065f46",
      backgroundColor: "#ecfdf5",
      border: "1px solid #a7f3d0",
      borderRadius: 2,
      px: 2,
      py: 1,
    }}
  >
    Account created successfully. Please log in.
  </Typography>
)}


        {/* ----------------------------------------------------------Error Message */}
        {error && (
          <Typography
            color="error"
            sx={{ mb: 2, fontSize: 14, whiteSpace: "pre-line" }}
          >
            {error}
          </Typography>
        )}

        {/* Login Form */}
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            inputRef={usernameRef}
            margin="normal"
            autoComplete="username"
          />

         <TextField
  fullWidth
  label="Password"
  name="password"
  type={showPassword ? "text" : "password"}
  value={formData.password}
  onChange={handleChange}
  margin="normal"
  autoComplete="current-password"
  InputProps={{
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          edge="end"
          onClick={() => setShowPassword((prev) => !prev)}
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  }}
/>

          <Box sx={{ textAlign: "right", mt: 1 }}>
            <Link
              component="button"
              type="button"
              onClick={() => navigate("/forgot-password")}
              sx={{ fontSize: 12, color: "#0057A4" }}
            >
              Forgot Password?
            </Link>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              bgcolor: "#0057A4",
              py: 1.2,
              fontWeight: "bold",
              "&:hover": { bgcolor: "#004a8d" },
            }}
            disabled={!formData.username || !formData.password}
          >
            Login
          </Button>

	<Typography sx={{ mt: 2, fontSize: 13, color: "text.secondary" }}>
  Need access? Contact your organization admin.
</Typography>

        </Box>
      </Paper>
    </Box>
  );
};

export default Login;
